/**
 * The ONE shared event form behind the two calendar entry points: `variant: 'sidebar'` is the
 * create/edit form on the /calendar drawer surface (EventSidebar hosts it), `variant: 'dialog'`
 * is the dashboard create dialog (ScheduleDialog hosts the Radix shell). One component holds the
 * unified draft state, the validation-on-submit and the submit builders. Behavior (the dynamic
 * form slice):
 *
 * - both variants at CREATE: the type cards (meeting/webinar radiogroup; the event does not
 *   store the kind, so EDIT shows none), the duration presets (30min / 1h / 1.5h / 2h /
 *   custom - "custom" reveals the end date+time field), the online-room toggle with the
 *   AUTO-GENERATED room slug (slugify(title) -> availability + planned-window check, suffix
 *   probing, regenerate) and the color picker trio (palette swatches + native picker + hex).
 * - webinar only (type-driven): the seats field (capacity) and the waiting-room switch; a
 *   meeting hides both. On EDIT the capacity shows only when the stored event has one.
 * - the recording toggle is gone everywhere: the create body never sends recordingIntent and
 *   the PATCH omits it, so the stored intent stays untouched.
 * - sidebar only: all-day mode (no time/duration - start/end dates only), edit mode (PATCH
 *   semantics, delete gate, read-only imported rows, occurrence note, read-only room slug);
 *   dialog only: the AD rooms picker (sets the room address manually, degrades to free text).
 *
 * Shared by both: title, location, description (4000-rune counter), the participant chips
 * picker (CR-34: a unified chips field in BOTH variants that suggests real Keycloak directory
 * users while typing and degrades to free text when the directory is down), toasts and the
 * draft -> wire flows (same create body shape, same PATCH key presence/omission).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent, JSX, KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Dices, Loader2, MapPin, Radio, Trash2, TriangleAlert, Users, Video } from 'lucide-react';

import { Button } from '../components/ui/button';
import { DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Switch } from '../components/ui/switch';
import { slugifyRoomName } from '../lib/slugify';
import { fetchRoomAvailability } from '../api/rooms';
import { searchDirectoryUsers, type DirectoryUser } from '../api/directory';
import {
  CalendarApiError,
  createEvent,
  deleteEvent,
  ensureRoomKind,
  listAdRooms,
  listEvents,
  patchEvent,
  type AdRoom,
  type CalendarEvent,
  type CreateEventBody,
  type PatchEventBody,
} from './api';
import { DateTimeField } from './DateTimeField';
import { EVENT_COLORS, isValidHexColor, normalizeHexColor } from './palette';
import { showToast } from './Toast';
import { eventEnd, eventKey, eventStart, findOverlappingEvents, formatTime } from './month';
import {
  MAX_CAPACITY,
  MAX_DESCRIPTION_RUNES,
  combineLocal,
  isValidDateString,
  isValidTimeString,
  parseCapacityInput,
  toRfc3339,
  validateDescription,
  validateParticipant,
  validateTitle,
} from './validation';

/** The room kinds the create flows can assert at the control service (ADR 0010, workshop merge). */
export type EventType = 'meeting' | 'webinar';

/** Label style shared by every form field heading. */
const LABEL_CLASS = 'text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground';

/** The duration presets of the mockup; "custom" swaps them for the end date+time field. */
const DURATION_PRESETS = [30, 60, 90, 120] as const;
const CUSTOM_DURATION = 'custom';

/** Debounce between the last title keystroke and the room-slug availability probing. */
const ROOM_GEN_DEBOUNCE_MS = 500;

/** Directory suggestions (CR-34): idle gap after the last keystroke, the minimum query and
 * how many options the picker asks the endpoint for (the server caps at 50). */
const PARTICIPANT_SUGGEST_DEBOUNCE_MS = 300;
const PARTICIPANT_SUGGEST_MIN_RUNES = 2;
const PARTICIPANT_SUGGEST_LIMIT = 8;

/** The planned-collision window of the room search: yesterday .. +90 days of events. */
const ROOM_PLANNED_PAST_MS = 86_400_000;
const ROOM_PLANNED_FUTURE_MS = 90 * 86_400_000;

/** Availability probes per generation pass; bounds the random-suffix tail. */
const MAX_CANDIDATE_PROBES = 24;

/** Value the native color input shows while no color is picked (it cannot render ''). */
const DEFAULT_COLOR_PICKER_HEX = '#ffffff';

interface TypeCard {
  readonly id: EventType;
  readonly labelKey: string;
  readonly hintKey: string;
  readonly icon: typeof Video;
}

const TYPE_CARDS: readonly TypeCard[] = [
  {
    id: 'meeting',
    labelKey: 'calendar.schedule.type_meeting',
    hintKey: 'calendar.schedule.type_meeting_hint',
    icon: Video,
  },
  {
    id: 'webinar',
    labelKey: 'calendar.schedule.type_webinar',
    hintKey: 'calendar.schedule.type_webinar_hint',
    icon: Radio,
  },
];

function pad2(value: number): string {
  return `${value}`.padStart(2, '0');
}

/** yyyy-mm-dd of a local date (the native date input's value). */
function toDateInputValue(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

/** HH:MM of a local date (the native time input's value). */
function toTimeInputValue(date: Date): string {
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

/** yyyy-mm-dd shifted by whole days (inclusive -> exclusive end conversion for all-day rows). */
function addDaysIso(dateIso: string, days: number): string {
  const [year = 1970, month = 1, day = 1] = dateIso.split('-').map(Number);
  const shifted = new Date(year, month - 1, day + days);
  return toDateInputValue(shifted);
}

/** Whole-day number of a yyyy-mm-dd string (local midnight); the start-shift delta uses it. */
function dayNumber(dateIso: string): number {
  const [year = 1970, month = 1, day = 1] = dateIso.split('-').map(Number);
  return new Date(year, month - 1, day).getTime() / 86_400_000;
}

/** One conflict row's text: "<title> · <window>", both dates when the window crosses days. */
function formatConflictRow(conflict: CalendarEvent, locale: string): string {
  const start = eventStart(conflict);
  const end = eventEnd(conflict);
  const day = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' });
  if (conflict.allDay) {
    return `${conflict.title} · ${day.format(start)}`;
  }
  const sameDay =
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth() &&
    start.getDate() === end.getDate();
  return sameDay
    ? `${conflict.title} · ${formatTime(start)}–${formatTime(end)}`
    : `${conflict.title} · ${day.format(start)} ${formatTime(start)} – ${day.format(end)} ${formatTime(end)}`;
}

/** Next full hour, so the sidebar create form's default is one click away. */
function defaultStartTime(date: Date): Date {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    (date.getHours() + 1) % 24,
    0,
  );
}

/** Default draft time of the dialog variant: the next full hour, so "create" is one click away. */
function defaultTime(now: Date): string {
  return `${pad2((now.getHours() + 1) % 24)}:00`;
}

/**
 * The candidate for attempt `seq` of a slug search: 1 = the bare title slug, 2..9 = the
 * numeric suffixes, past that a random 4-digit suffix (the deterministic range is exhausted).
 */
function nextRoomCandidate(base: string, seq: number): string {
  if (seq <= 1) {
    return base;
  }
  if (seq <= 9) {
    return `${base}-${seq}`;
  }
  return `${base}-${1000 + Math.floor(Math.random() * 9000)}`;
}

export interface EventSidebarInitial {
  /** Picked range start (a day cell or a drag-select begin). */
  readonly start: Date;
  /** Picked range end; FullCalendar's all-day selection ends exclusive. */
  readonly end: Date;
  readonly allDay: boolean;
}

/** Neutral sidebar create prefill: today at the next full hour for one hour. */
export function zeroInitial(): EventSidebarInitial {
  const start = defaultStartTime(new Date());
  return { start, end: new Date(start.getTime() + 3_600_000), allDay: false };
}

/**
 * Slugifies an AD room for the roomSlug field: the mail's local part (or the name) lowered,
 * every run of foreign characters collapsed to one hyphen, trimmed, clamped to the slug bounds.
 * A room whose mail cannot be slugified is skipped by the picker, never half-filled.
 */
export function slugifyAdRoom(room: AdRoom): string | null {
  const source = room.mail !== null ? (room.mail.split('@')[0] ?? '') : room.name;
  const slug = source
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64)
    .replace(/^-+|-+$/g, '');
  return /^[a-z0-9][a-z0-9-]{2,63}$/.test(slug) ? slug : null;
}

export interface EventSidebarFormProps {
  /** Fresh bearer token from the SSO session; the form assumes it is valid. */
  readonly token: string;
  /** Create prefill (picked window); ignored while `event` is set. */
  readonly initial?: EventSidebarInitial | null | undefined;
  /** Edit target; non-null switches the form into edit mode. */
  readonly event?: CalendarEvent | null;
  /** The events already loaded for the visible calendar window: the conflict check runs
   * against these client-side (no extra endpoint); absent list = no warnings. */
  readonly allEvents?: readonly CalendarEvent[];
  /** Called after a successful create/patch; the sidebar closes itself and refetches. */
  readonly onSaved: (event: CalendarEvent) => void;
  /** Called after a successful delete. */
  readonly onDeleted: (id: number) => void;
  /** Closes the drawer (save/delete success paths). */
  readonly onClose: () => void;
  /** The cancel button: closes the drawer, or returns to the details view it came from. */
  readonly onCancel: () => void;
}

export interface DialogEventFormProps {
  /** Fresh bearer token from the SSO session; the dialog assumes it is valid. */
  readonly token: string;
  /** Controlled open state of the hosting Radix dialog. */
  readonly open: boolean;
  /** Closes the dialog (the create success path and the cancel button). */
  readonly onOpenChange: (open: boolean) => void;
  /** Type card preselected by the caller (e.g. webinar from the quick action). */
  readonly initialType?: EventType;
  /** Called after a successful create so the page can refresh its range. */
  readonly onCreated: (event: CalendarEvent) => void;
}

/** Discriminated props of the shared form; each wrapper feeds its own member verbatim. */
export type EventFormProps =
  | ({ readonly variant: 'sidebar' } & EventSidebarFormProps)
  | ({ readonly variant: 'dialog' } & DialogEventFormProps);

/**
 * The unified draft: one window model for both variants (start date+time, duration choice,
 * end date+time for the custom path and the all-day span). `lobbyIntent` carries the per-room
 * lobby option (CR-14/CR-21); `recordingIntent` is retired from the form - the stored value is
 * never touched again. `onlineRoom` gates the auto-generated room block (create only).
 */
interface EventFormDraft {
  title: string;
  /** Start of the window: date yyyy-mm-dd, time HH:MM ('' only while the draft is invalid). */
  startDate: string;
  startTime: string;
  /** End of the window; authoritative only for the custom duration and the all-day span. */
  endDate: string;
  endTime: string;
  allDay: boolean;
  /** Duration preset in minutes, or 'custom' (the end fields take over). */
  durationChoice: string;
  location: string;
  /** The effective room slug: auto-generated at create, stored read-only at edit. */
  roomSlug: string;
  kind: EventType;
  /** Selected palette color ('' = no color); the custom inputs write here once valid. */
  color: string;
  lobbyIntent: boolean;
  /** Long-form body text (CR-27): markdown-ish plain text, the webinar detail page renders it. */
  description: string;
  /** The registration cap as typed (CR-27): '' = unlimited. */
  capacity: string;
  /** Online-room gate (create only): OFF removes the room block and the room from the body. */
  onlineRoom: boolean;
}

/** Sidebar draft for a create prefill; the all-day end input is inclusive, so one day is subtracted. */
function draftFromInitial(initial: EventSidebarInitial): EventFormDraft {
  const start = initial.allDay ? defaultStartTime(initial.start) : initial.start;
  const end = initial.allDay ? defaultStartTime(initial.end) : initial.end;
  const safeEnd = end.getTime() > start.getTime() ? end : new Date(start.getTime() + 3_600_000);
  return {
    title: '',
    startDate: toDateInputValue(start),
    startTime: toTimeInputValue(start),
    endDate: initial.allDay ? addDaysIso(toDateInputValue(safeEnd), -1) : toDateInputValue(safeEnd),
    endTime: initial.allDay ? '00:00' : toTimeInputValue(safeEnd),
    allDay: initial.allDay,
    durationChoice: '60',
    location: '',
    roomSlug: '',
    kind: 'meeting',
    color: '',
    lobbyIntent: false,
    description: '',
    capacity: '',
    onlineRoom: true,
  };
}

/**
 * Sidebar draft pre-filled from an existing event; occurrences edit the series base window.
 * The duration opens as "custom" so the stored end stays authoritative and editable, and the
 * stored room slug renders read-only (the kind was never stored, so no type cards either).
 */
function draftFromEvent(event: CalendarEvent): EventFormDraft {
  const start = new Date(event.startsAt);
  const end = new Date(event.endsAt);
  return {
    title: event.title,
    startDate: toDateInputValue(start),
    startTime: toTimeInputValue(start),
    endDate: event.allDay ? addDaysIso(toDateInputValue(end), -1) : toDateInputValue(end),
    endTime: toTimeInputValue(end),
    allDay: event.allDay,
    durationChoice: CUSTOM_DURATION,
    location: event.location ?? '',
    roomSlug: event.roomSlug ?? '',
    kind: 'meeting',
    color: event.color ?? '',
    lobbyIntent: event.lobbyIntent,
    description: event.description ?? '',
    capacity: event.capacity !== null ? String(event.capacity) : '',
    onlineRoom: true,
  };
}

/** Dialog draft: today at the next full hour for the 60-minute preset. */
function draftFromDialog(initialType: EventType): EventFormDraft {
  const now = new Date();
  const date = toDateInputValue(now);
  const time = defaultTime(now);
  return {
    title: '',
    startDate: date,
    startTime: time,
    endDate: date,
    endTime: time,
    allDay: false,
    durationChoice: '60',
    location: '',
    roomSlug: '',
    kind: initialType,
    color: '',
    lobbyIntent: false,
    description: '',
    capacity: '',
    onlineRoom: true,
  };
}

/** Arms the draft once from the props at mount time; a stale draft never leaks between opens. */
function initDraft(props: EventFormProps): EventFormDraft {
  if (props.variant === 'sidebar') {
    const target = props.event ?? null;
    return target !== null
      ? draftFromEvent(target)
      : draftFromInitial(props.initial ?? zeroInitial());
  }
  return draftFromDialog(props.initialType ?? 'meeting');
}

/** Local RFC 3339 start of the draft window (midnight for the all-day form). */
function draftStartAt(draft: EventFormDraft): Date {
  return draft.allDay
    ? combineLocal(draft.startDate, '')
    : combineLocal(draft.startDate, draft.startTime);
}

/**
 * Local RFC 3339 end of the draft window. A preset recomputes the end from the start on every
 * read (selecting a preset and moving the start both keep the duration); "custom" and the
 * all-day span read the stored end fields (the all-day end date is inclusive).
 */
function draftEndAt(draft: EventFormDraft): Date {
  if (draft.allDay) {
    const nextDay = new Date(combineLocal(draft.endDate, '').getTime() + 24 * 3_600_000);
    return nextDay;
  }
  if (draft.durationChoice !== CUSTOM_DURATION) {
    const start = draftStartAt(draft);
    return new Date(start.getTime() + Number(draft.durationChoice) * 60_000);
  }
  return combineLocal(draft.endDate, draft.endTime);
}

/** A field error line; every "role=alert" paragraph of the two forms shares this exact style. */
function FieldErrorText(props: { readonly message: string | undefined }): JSX.Element | null {
  if (props.message === undefined) {
    return null;
  }
  return (
    <p role="alert" className="mt-1 text-[11.5px] text-red-400">
      {props.message}
    </p>
  );
}

/** Title input - identical markup in both variants, only the input id differs. */
function TitleField(props: {
  readonly id: string;
  readonly label: string;
  readonly placeholder: string;
  readonly value: string;
  readonly error: string | undefined;
  readonly onChange: (value: string) => void;
}): JSX.Element {
  return (
    <div>
      <Label htmlFor={props.id} className={LABEL_CLASS}>
        {props.label}
      </Label>
      <Input
        id={props.id}
        value={props.value}
        onChange={(input) => props.onChange(input.target.value)}
        placeholder={props.placeholder}
        aria-invalid={props.error !== undefined}
        className="mt-2 h-10 rounded-lg text-[13.5px]"
      />
      <FieldErrorText message={props.error} />
    </div>
  );
}

/** Location input; the dialog variant wraps it with the decorative map-pin icon. */
function LocationField(props: {
  readonly id: string;
  readonly label: string;
  readonly placeholder: string;
  readonly value: string;
  readonly withIcon: boolean;
  readonly onChange: (value: string) => void;
}): JSX.Element {
  const input = (
    <Input
      id={props.id}
      value={props.value}
      onChange={(input) => props.onChange(input.target.value)}
      placeholder={props.placeholder}
      className={
        props.withIcon ? 'h-10 rounded-lg pr-9 text-[13.5px]' : 'mt-2 h-10 rounded-lg text-[13.5px]'
      }
    />
  );
  return (
    <div>
      <Label htmlFor={props.id} className={LABEL_CLASS}>
        {props.label}
      </Label>
      {props.withIcon ? (
        <div className="relative mt-2">
          {input}
          <MapPin
            aria-hidden
            className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          />
        </div>
      ) : (
        input
      )}
    </div>
  );
}

/** The long-form body textarea with the rune counter (CR-27) - identical in both variants. */
function DescriptionField(props: {
  readonly id: string;
  readonly testid: string;
  readonly label: string;
  readonly placeholder: string;
  readonly value: string;
  readonly error: string | undefined;
  readonly onChange: (value: string) => void;
}): JSX.Element {
  return (
    <div>
      <div className="flex items-center justify-between">
        <Label htmlFor={props.id} className={LABEL_CLASS}>
          {props.label}
        </Label>
        <span className="text-[11px] tabular-nums text-muted-foreground">
          {[...props.value].length}/{MAX_DESCRIPTION_RUNES}
        </span>
      </div>
      <textarea
        id={props.id}
        data-testid={props.testid}
        value={props.value}
        onChange={(input) => props.onChange(input.target.value)}
        placeholder={props.placeholder}
        maxLength={MAX_DESCRIPTION_RUNES}
        rows={4}
        aria-invalid={props.error !== undefined}
        className="mt-2 w-full rounded-lg border border-border bg-card px-3 py-2 text-[13.5px] text-foreground outline-none placeholder:text-muted-foreground focus:border-ring"
      />
      <FieldErrorText message={props.error} />
    </div>
  );
}

/** The registration cap input (CR-27); empty = unlimited - identical in both variants. */
function CapacityField(props: {
  readonly id: string;
  readonly testid: string;
  readonly label: string;
  readonly placeholder: string;
  readonly value: string;
  readonly error: string | undefined;
  readonly onChange: (value: string) => void;
}): JSX.Element {
  return (
    <div>
      <Label htmlFor={props.id} className={LABEL_CLASS}>
        {props.label}
      </Label>
      <Input
        id={props.id}
        data-testid={props.testid}
        type="number"
        min={0}
        max={MAX_CAPACITY}
        value={props.value}
        onChange={(input) => props.onChange(input.target.value)}
        placeholder={props.placeholder}
        aria-invalid={props.error !== undefined}
        className="mt-2 h-10 rounded-lg text-[13.5px] tabular-nums"
      />
      <FieldErrorText message={props.error} />
    </div>
  );
}

/** The meeting/webinar type cards: create-only in BOTH variants (the event stores no kind). */
function TypeCardsField(props: {
  readonly active: EventType;
  readonly onSelect: (kind: EventType) => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div>
      <Label className={LABEL_CLASS}>{t('calendar.schedule.type_label')}</Label>
      <div
        className="mt-2 grid grid-cols-2 gap-2"
        role="radiogroup"
        aria-label={t('calendar.schedule.type_label')}
      >
        {TYPE_CARDS.map((card) => {
          const active = props.active === card.id;
          const Icon = card.icon;
          return (
            <button
              key={card.id}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => props.onSelect(card.id)}
              className={
                active
                  ? 'rounded-xl border border-primary bg-primary p-3 text-left text-primary-foreground shadow-sm transition-all'
                  : 'rounded-xl border border-border bg-card p-3 text-left text-foreground transition-all hover:border-muted-foreground/30'
              }
            >
              <Icon
                className={
                  active ? 'size-4 text-primary-foreground' : 'size-4 text-muted-foreground'
                }
              />
              <div className="mt-2.5 text-[13px] font-semibold">{t(card.labelKey)}</div>
              <div
                className={
                  active
                    ? 'text-[10.5px] text-primary-foreground/60'
                    : 'text-[10.5px] text-muted-foreground'
                }
              >
                {t(card.hintKey)}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** The waiting-room switch row: webinar-only at create, room-bound (and lockable) at edit. */
function LobbySwitchField(props: {
  readonly id: string;
  readonly testid: string;
  readonly checked: boolean;
  readonly disabled: boolean;
  readonly hint: string;
  readonly hintTestid?: string;
  readonly onChange: (checked: boolean) => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <Label htmlFor={props.id} className="text-[12px] font-medium text-foreground">
          {t('calendar.event.lobby_label')}
        </Label>
        <p className="text-[11px] text-muted-foreground" data-testid={props.hintTestid}>
          {props.hint}
        </p>
      </div>
      <Switch
        id={props.id}
        checked={props.checked}
        disabled={props.disabled}
        onCheckedChange={props.onChange}
        data-testid={props.testid}
        aria-label={t('calendar.event.lobby_label')}
      />
    </div>
  );
}

/**
 * The roster entry a directory user becomes when picked (CR-34): "FullName <email>", the
 * e-mail skipped when the realm user has none. fullName carries the server's username fallback,
 * but the client re-applies it defensively.
 */
export function composeDirectoryChip(user: DirectoryUser): string {
  const label = user.fullName.trim().length > 0 ? user.fullName.trim() : user.username.trim();
  const email = user.email.trim();
  return email.length > 0 ? `${label} <${email}>` : label;
}

/**
 * The unified participant picker (both variants, CR-34): a chips input whose single-line field
 * suggests REAL directory users after two typed runes (300 ms debounce, up to 8 options, the
 * composed "FullName <email>" chip on pick, keyboard navigation, Escape dismiss). Free text
 * still adds on Enter / comma / the add button, so the field degrades naturally: the first
 * directory failure (503/502/network/unauthorized) switches the picker to free-text-only for
 * the rest of the form's life and shows the muted hint - no suggestion is ever fetched again.
 * The roster rules (50 entries x 200 runes, case-insensitive dedup) ride validateParticipant.
 */
function ParticipantsPickerField(props: {
  readonly id: string;
  readonly token: string;
  readonly participants: readonly string[];
  readonly onChange: (participants: readonly string[]) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<readonly DirectoryUser[]>([]);
  const [suggestState, setSuggestState] = useState<'idle' | 'loading' | 'ready'>('idle');
  const [dismissed, setDismissed] = useState(false);
  const [down, setDown] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const searchSeq = useRef(0);

  const query = input.trim();
  const queryReady = [...query].length >= PARTICIPANT_SUGGEST_MIN_RUNES;
  const open = !down && !dismissed && queryReady && suggestState !== 'idle';

  useEffect(() => {
    if (down) {
      return;
    }
    if (!queryReady) {
      searchSeq.current += 1;
      setSuggestions([]);
      setSuggestState('idle');
      return;
    }
    setSuggestState('loading');
    const timer = setTimeout(() => {
      const run = ++searchSeq.current;
      searchDirectoryUsers(props.token, query, { limit: PARTICIPANT_SUGGEST_LIMIT })
        .then((users) => {
          if (run !== searchSeq.current) {
            return;
          }
          setSuggestions(users);
          setSuggestState('ready');
          setHighlight(0);
        })
        .catch(() => {
          if (run !== searchSeq.current) {
            return;
          }
          // Degrade silently: free text keeps working, the hint explains, and `down` stops
          // every future suggestion fetch for this form's lifetime.
          setDown(true);
          setSuggestions([]);
          setSuggestState('idle');
        });
    }, PARTICIPANT_SUGGEST_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      searchSeq.current += 1;
    };
    // The roster rides no dep on purpose: the dedup against already-added chips happens at
    // render time, so a chip add never re-arms the debounced search (only typing does).
  }, [query, queryReady, down, props.token]);

  // Already-added entries never show up as suggestions (case-insensitive on the chip string).
  const added = new Set(props.participants.map((chip) => chip.toLowerCase()));
  const visible = suggestions
    .filter((user) => !added.has(composeDirectoryChip(user).toLowerCase()))
    .slice(0, PARTICIPANT_SUGGEST_LIMIT);
  const active = Math.min(highlight, Math.max(0, visible.length - 1));

  /** Adds one roster entry through the shared rules; reports failure so the caller keeps text. */
  function addValue(raw: string): boolean {
    const value = raw.trim();
    const validation = validateParticipant(value, props.participants);
    if (validation !== null) {
      setError(t(validation));
      return false;
    }
    setError(null);
    props.onChange([...props.participants, value]);
    return true;
  }

  /** Enter / comma / the add button: the typed text becomes a chip (blank -> the alert). */
  function addFromInput(): void {
    if (input.trim().length === 0) {
      setError(t('calendar.schedule.errors.participant_blank'));
      return;
    }
    if (addValue(input)) {
      setInput('');
    }
  }

  function pickSuggestion(user: DirectoryUser): void {
    addValue(composeDirectoryChip(user));
    setInput('');
    setSuggestions([]);
    setSuggestState('idle');
    setDismissed(false);
    setHighlight(0);
  }

  function removeChip(name: string): void {
    props.onChange(props.participants.filter((chip) => chip !== name));
  }

  function handleKeyDown(keyEvent: KeyboardEvent<HTMLInputElement>): void {
    if (keyEvent.key === 'ArrowDown' && open && visible.length > 0) {
      keyEvent.preventDefault();
      setDismissed(false);
      setHighlight(Math.min(active + 1, visible.length - 1));
      return;
    }
    if (keyEvent.key === 'ArrowUp' && open && visible.length > 0) {
      keyEvent.preventDefault();
      setHighlight(Math.max(active - 1, 0));
      return;
    }
    if (keyEvent.key === 'Escape' && open) {
      // The dialog variant would otherwise take Escape as "close the whole dialog".
      keyEvent.preventDefault();
      keyEvent.stopPropagation();
      setDismissed(true);
      return;
    }
    if (keyEvent.key === 'Enter') {
      keyEvent.preventDefault();
      const picked = open ? visible[active] : undefined;
      if (picked !== undefined) {
        pickSuggestion(picked);
      } else {
        addFromInput();
      }
      return;
    }
    if (keyEvent.key === ',') {
      keyEvent.preventDefault();
      addFromInput();
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <Label htmlFor={props.id} className={LABEL_CLASS}>
          {t('calendar.schedule.participants_label')}
        </Label>
        <span className="text-[11px] tabular-nums text-muted-foreground">
          {t('calendar.schedule.participants_selected', { count: props.participants.length })}
        </span>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-1.5 rounded-lg border border-border p-2">
        {props.participants.map((chip) => (
          <span
            key={chip}
            className="inline-flex items-center gap-1.5 rounded-full bg-muted py-1 pl-2.5 pr-2 text-[12px] font-medium text-foreground"
          >
            {chip}
            <button
              type="button"
              onClick={() => removeChip(chip)}
              aria-label={t('calendar.schedule.participants_remove', { name: chip })}
              className="text-muted-foreground hover:text-foreground"
            >
              ×
            </button>
          </span>
        ))}
        <div className="relative flex min-w-[160px] flex-1 items-center gap-1.5">
          <Input
            id={props.id}
            data-testid="event-participants"
            role="combobox"
            aria-expanded={open}
            aria-controls={open ? `${props.id}-listbox` : undefined}
            aria-autocomplete="list"
            aria-invalid={error !== null}
            value={input}
            onChange={(inputEvent) => {
              setInput(inputEvent.target.value);
              setError(null);
              setDismissed(false);
              setHighlight(0);
            }}
            onKeyDown={handleKeyDown}
            placeholder={t('calendar.schedule.participants_search_placeholder')}
            className="h-7 min-w-0 flex-1 border-none bg-transparent px-1 text-[12.5px] shadow-none focus-visible:ring-0"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 shrink-0 gap-1 px-2 text-[12px]"
            onClick={addFromInput}
          >
            <Users aria-hidden className="size-3" />
            {t('calendar.schedule.participants_add')}
          </Button>
          {open ? (
            <div
              id={`${props.id}-listbox`}
              role="listbox"
              aria-label={t('calendar.schedule.participants_label')}
              data-testid="event-participants-suggestions"
              className="absolute left-0 right-0 top-full z-20 mt-1 overflow-hidden rounded-lg border border-border bg-popover text-left shadow-md"
            >
              {suggestState === 'loading' ? (
                <p className="px-3 py-2 text-[12px] text-muted-foreground">
                  {t('calendar.schedule.participants_searching')}
                </p>
              ) : visible.length === 0 ? (
                <p className="px-3 py-2 text-[12px] text-muted-foreground">
                  {t('calendar.schedule.participants_no_matches')}
                </p>
              ) : (
                visible.map((user, index) => (
                  <button
                    key={user.id}
                    type="button"
                    role="option"
                    id={`${props.id}-option-${index}`}
                    aria-selected={index === active}
                    onMouseDown={(mouseEvent) => mouseEvent.preventDefault()}
                    onClick={() => pickSuggestion(user)}
                    className={
                      index === active
                        ? 'flex w-full items-baseline justify-between gap-2 bg-accent px-3 py-1.5'
                        : 'flex w-full items-baseline justify-between gap-2 px-3 py-1.5'
                    }
                  >
                    <span className="truncate text-[12.5px] text-foreground">
                      {composeDirectoryChip(user)}
                    </span>
                    {user.email.trim().length > 0 ? (
                      <span className="shrink-0 text-[11px] text-muted-foreground">
                        {user.email}
                      </span>
                    ) : null}
                  </button>
                ))
              )}
            </div>
          ) : null}
        </div>
      </div>
      {down ? (
        <p
          data-testid="event-participants-hint"
          className="mt-1 text-[11px] text-muted-foreground"
        >
          {t('calendar.schedule.participants_directory_unavailable')}
        </p>
      ) : null}
      {error !== null ? <FieldErrorText message={error} /> : null}
    </div>
  );
}

/** Generation state of the auto room slug (create only). */
interface RoomGeneration {
  /** Attempt index the current slug was found at; regenerate bumps it. */
  readonly seq: number;
  readonly slug: string;
  readonly status: 'idle' | 'checking' | 'free' | 'suggested' | 'title_needed';
}

const INITIAL_ROOM_GENERATION: RoomGeneration = { seq: 1, slug: '', status: 'idle' };

export function EventForm(props: EventFormProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const isRu = i18n.language.startsWith('ru');
  const locale = isRu ? 'ru-RU' : 'en-US';
  const token = props.token;

  const event = props.variant === 'sidebar' ? (props.event ?? null) : null;
  const editing = event !== null;
  const readOnly = event !== null && event.source !== 'local';
  const isOccurrence = event !== null && event.occurrenceStartsAt !== null;
  // Dialog-narrowed views of the props, so the effects below have stable primitive deps.
  const dialogOpen = props.variant === 'dialog' ? props.open : false;
  const dialogInitialType: EventType =
    props.variant === 'dialog' ? (props.initialType ?? 'meeting') : 'meeting';

  const [draft, setDraft] = useState<EventFormDraft>(() => initDraft(props));
  // Participant roster of BOTH variants: chips added through the shared picker (free text on
  // Enter/comma/the add button, directory picks on suggestion click); edit mode starts from
  // the stored strings.
  const [participants, setParticipants] = useState<readonly string[]>(() =>
    event !== null ? [...event.participants] : [],
  );
  const [customColor, setCustomColor] = useState('');
  const [customColorError, setCustomColorError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Readonly<Record<string, string>>>({});
  const [pending, setPending] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // The auto room slug (create only): a debounce-delayed search over the title slug, an
  // attempt sequence the regenerate button bumps, and a manual override the AD picker arms.
  const [roomGen, setRoomGen] = useState<RoomGeneration>(INITIAL_ROOM_GENERATION);
  const [roomManual, setRoomManual] = useState(false);
  const roomGenRunRef = useRef(0);

  // The AD room picker loads lazily, once per mount, when the dialog opens; a failure just
  // empties the list (the auto-generated slug remains usable).
  const [rooms, setRooms] = useState<readonly AdRoom[]>([]);
  const [roomsState, setRoomsState] = useState<'idle' | 'loading' | 'ready'>('idle');
  const roomsRequested = useRef(false);

  useEffect(() => {
    // Re-arm the dialog's type card when the dialog is (re)opened with a caller-preset.
    if (dialogOpen) {
      setDraftField('kind', dialogInitialType);
    }
  }, [dialogOpen, dialogInitialType]);

  useEffect(() => {
    if (!dialogOpen || roomsRequested.current) {
      return;
    }
    roomsRequested.current = true;
    setRoomsState('loading');
    listAdRooms(token)
      .then((loaded) => {
        setRooms(loaded);
        setRoomsState('ready');
      })
      .catch(() => {
        setRooms([]);
        setRoomsState('ready');
      });
  }, [dialogOpen, token]);

  // The color trio stays synchronized: a swatch click or a native-picker change mirrors the
  // canonical hex into the text input (typing alone never fires this - draft.color only moves
  // through pickColor/applyCustomColor).
  useEffect(() => {
    setCustomColor(draft.color);
  }, [draft.color]);

  const sourceBadge = useMemo(() => {
    if (event === null || event.source === 'local') {
      return null;
    }
    return event.source === 'caldav'
      ? 'CalDAV'
      : event.source === 'ews'
        ? t('calendar.list.source_ews')
        : t('calendar.list.source_ad');
  }, [event, t]);

  function setDraftField<K extends keyof EventFormDraft>(key: K, value: EventFormDraft[K]): void {
    setDraft((previous) => ({ ...previous, [key]: value }));
  }

  // Room search inputs, recomputed per render for the generation effect below.
  const canAutoRoom = !editing && !readOnly && draft.onlineRoom && !roomManual;
  const titleSlug = useMemo(() => slugifyRoomName(draft.title.trim()), [draft.title]);

  useEffect(() => {
    if (!canAutoRoom) {
      return;
    }
    if (titleSlug === null) {
      setRoomGen({ seq: 1, slug: '', status: 'title_needed' });
      setDraftField('roomSlug', '');
      return;
    }
    const startSeq = roomGen.seq;
    const timer = setTimeout(() => {
      void generateRoomSlug(titleSlug, startSeq);
    }, ROOM_GEN_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // The generator reads token/state through stable setters; the search re-runs per title
    // keystroke, attempt bump and gate flip.
  }, [canAutoRoom, titleSlug, roomGen.seq, token]);

  /** Slugs of the events planned in the wide window (yesterday .. +90d); best effort. */
  async function plannedRoomSlugs(run: number): Promise<Set<string>> {
    try {
      const now = Date.now();
      const events = await listEvents(token, {
        from: toRfc3339(new Date(now - ROOM_PLANNED_PAST_MS)),
        to: toRfc3339(new Date(now + ROOM_PLANNED_FUTURE_MS)),
      });
      if (run !== roomGenRunRef.current) {
        return new Set<string>();
      }
      return new Set(events.flatMap((row) => (row.roomSlug !== null ? [row.roomSlug] : [])));
    } catch {
      // The planned-window check is best effort; the availability probe still runs.
      return new Set<string>();
    }
  }

  /**
   * Probes candidates from `startSeq` until one is free (not planned AND available on the
   * public endpoint), then writes it into the draft. Stale runs (a newer keystroke or a
   * regenerate) bail out through the run counter; an unreachable checker leaves the candidate
   * in play - the create path still guards collisions server-side.
   */
  async function generateRoomSlug(base: string, startSeq: number): Promise<void> {
    const run = ++roomGenRunRef.current;
    setRoomGen((current) => ({ ...current, status: 'checking' }));
    const planned = await plannedRoomSlugs(run);
    if (run !== roomGenRunRef.current) {
      return;
    }
    for (let seq = startSeq, probed = 0; probed < MAX_CANDIDATE_PROBES; seq += 1) {
      const candidate = nextRoomCandidate(base, seq);
      if (planned.has(candidate)) {
        continue;
      }
      probed += 1;
      let free = true;
      try {
        free = (await fetchRoomAvailability(candidate)).available;
      } catch {
        free = true;
      }
      if (run !== roomGenRunRef.current) {
        return;
      }
      if (free) {
        setRoomGen({ seq, slug: candidate, status: candidate === base ? 'free' : 'suggested' });
        setDraftField('roomSlug', candidate);
        return;
      }
    }
    // Every probe collided (unreachable in practice): stop spinning, keep the last state.
    setRoomGen((current) => ({ ...current, status: 'idle' }));
  }

  /**
   * Regenerate: jump to the next free candidate after the one currently shown. Bumping the
   * run counter also strands any in-flight search.
   */
  function regenerateRoomSlug(): void {
    roomGenRunRef.current += 1;
    setRoomManual(false);
    setRoomGen((current) => ({ ...current, seq: current.seq + 1 }));
  }

  /** The online-room gate: OFF strands an in-flight search and collapses the block. */
  function setOnlineRoom(checked: boolean): void {
    roomGenRunRef.current += 1;
    setDraftField('onlineRoom', checked);
  }

  /** AD picker pick: the directory room overrides the auto slug as a manual address. */
  function pickAdRoom(slug: string): void {
    roomGenRunRef.current += 1;
    setRoomManual(true);
    setDraftField('roomSlug', slug);
  }

  /**
   * Sidebar: editing the START shifts the END by the same delta so the event keeps its
   * duration (Google/Outlook behavior) - the story is "moved 13:00-14:00 to 15:00" ->
   * 15:00-16:00, never an inverted window; a previously inverted window is treated as one
   * hour. Under a duration preset the end is derived, so only the start moves; the all-day
   * form shifts the end DATE by the same day span.
   */
  function setStartDateTime(next: { readonly date: string; readonly time: string }): void {
    setDraft((previous) => {
      if (previous.allDay) {
        const deltaDays = Math.round(dayNumber(next.date) - dayNumber(previous.startDate));
        return {
          ...previous,
          startDate: next.date,
          endDate: addDaysIso(previous.endDate, deltaDays),
        };
      }
      if (previous.durationChoice !== CUSTOM_DURATION) {
        return { ...previous, startDate: next.date, startTime: next.time };
      }
      const oldStart = combineLocal(previous.startDate, previous.startTime);
      const newStart = combineLocal(next.date, next.time);
      const nextDraft = { ...previous, startDate: next.date, startTime: next.time };
      if (Number.isNaN(oldStart.getTime()) || Number.isNaN(newStart.getTime())) {
        return nextDraft;
      }
      const oldEnd = combineLocal(previous.endDate, previous.endTime);
      const durationMs = Math.max(oldEnd.getTime() - oldStart.getTime(), 3_600_000);
      const newEnd = new Date(newStart.getTime() + durationMs);
      return {
        ...nextDraft,
        endDate: toDateInputValue(newEnd),
        endTime: toTimeInputValue(newEnd),
      };
    });
  }

  /** The end fields are authoritative only for the custom duration and the all-day span. */
  function setEndDateTime(next: { readonly date: string; readonly time: string }): void {
    setDraft((previous) => ({
      ...previous,
      endDate: next.date,
      ...(previous.allDay ? {} : { endTime: next.time }),
    }));
  }

  /** Selecting a preset recomputes the stored end from the start (custom keeps the fields). */
  function pickDuration(choice: string): void {
    if (choice === CUSTOM_DURATION) {
      setDraftField('durationChoice', CUSTOM_DURATION);
      return;
    }
    setDraft((previous) => {
      const start = combineLocal(previous.startDate, previous.allDay ? '' : previous.startTime);
      const end = new Date(start.getTime() + Number(choice) * 60_000);
      return {
        ...previous,
        durationChoice: choice,
        endDate: toDateInputValue(end),
        endTime: toTimeInputValue(end),
      };
    });
  }

  /** Type card pick; a meeting drops the webinar-only authoring state (seats, lobby). */
  function pickKind(kind: EventType): void {
    setDraft((previous) => ({
      ...previous,
      kind,
      ...(kind === 'meeting' ? { capacity: '', lobbyIntent: false } : {}),
    }));
  }

  function pickColor(hex: string): void {
    if (readOnly) {
      return;
    }
    // Canonical lowercase form: the palette swatches and the pickers converge on one shape.
    setDraftField('color', normalizeHexColor(hex) ?? '');
    setCustomColorError(null);
  }

  function applyCustomColor(): void {
    if (customColor.trim().length === 0) {
      return;
    }
    const normalized = normalizeHexColor(customColor);
    if (normalized === null) {
      setCustomColorError(t('calendar.event.errors.color_invalid'));
      return;
    }
    setCustomColorError(null);
    setDraftField('color', normalized);
  }

  /**
   * Shared submit validation. The end input matters only for the custom duration and the
   * all-day span (a preset derives its end); the capacity validates only while its field is
   * visible (webinar at create, a stored cap at edit). The participant roster needs no submit
   * check: every chip passed validateParticipant (50 x 200, dedup) at add time.
   */
  function collectErrors(options: {
    readonly capacityVisible: boolean;
  }): {
    readonly errors: Record<string, string>;
    readonly capacityValue: number | null;
  } {
    const errors: Record<string, string> = {};
    const titleError = validateTitle(draft.title);
    if (titleError !== null) {
      errors.title = t(titleError);
    }
    const endInputShown = draft.allDay || draft.durationChoice === CUSTOM_DURATION;
    if (
      !isValidDateString(draft.startDate) ||
      (endInputShown && !isValidDateString(draft.endDate))
    ) {
      errors.date = t('calendar.schedule.errors.date_invalid');
    }
    if (
      (!draft.allDay && !isValidTimeString(draft.startTime)) ||
      (!draft.allDay &&
        draft.durationChoice === CUSTOM_DURATION &&
        !isValidTimeString(draft.endTime))
    ) {
      errors.time = t('calendar.schedule.errors.time_invalid');
    }
    if (Object.keys(errors).length === 0) {
      const startAt = draftStartAt(draft);
      const endAt = draftEndAt(draft);
      if (endAt.getTime() <= startAt.getTime()) {
        errors.range = t('calendar.event.errors.end_before_start');
      }
    }
    if (customColor.trim().length > 0 && !isValidHexColor(customColor)) {
      errors.color = t('calendar.event.errors.color_invalid');
    }
    // CR-27: the description respects the service's 4000-rune cap; the capacity parses
    // client-side ('' = unlimited), so bad input never reaches the wire.
    const descriptionError = validateDescription(draft.description);
    if (descriptionError !== null) {
      errors.description = t(descriptionError);
    }
    const capacityValue = parseCapacityInput(draft.capacity);
    if (options.capacityVisible && capacityValue === null) {
      errors.capacity = t('calendar.schedule.errors.capacity_invalid');
    }
    return { errors, capacityValue };
  }

  /**
   * The create body: identical for both variants. The room rides only when the online-room
   * gate is ON and a slug was resolved; the capacity only for a webinar (0/'' = unlimited =
   * absent); the lobby intent only when armed AND a room rides; recordingIntent never.
   */
  function buildCreateBody(inputs: {
    readonly capacityValue: number | null;
    readonly participants: readonly string[];
  }): CreateEventBody {
    const location = draft.location.trim();
    const roomSlug = draft.onlineRoom ? draft.roomSlug.trim() : '';
    const description = draft.description.trim();
    return {
      title: draft.title.trim(),
      startsAt: toRfc3339(draftStartAt(draft)),
      endsAt: toRfc3339(draftEndAt(draft)),
      allDay: draft.allDay,
      ...(location.length > 0 ? { location } : {}),
      ...(roomSlug.length > 0 ? { roomSlug } : {}),
      ...(inputs.participants.length > 0 ? { participants: inputs.participants } : {}),
      ...(draft.color.length > 0 ? { color: draft.color } : {}),
      ...(description.length > 0 ? { description } : {}),
      ...(draft.kind === 'webinar' && inputs.capacityValue !== null && inputs.capacityValue > 0
        ? { capacity: inputs.capacityValue }
        : {}),
      ...(draft.lobbyIntent && roomSlug.length > 0 ? { lobbyIntent: true } : {}),
    };
  }

  /** Sidebar: the delete confirm gate's second step (PATCH forms only). */
  async function runSidebarDelete(side: EventSidebarFormProps): Promise<void> {
    if (event === null || deleting) {
      return;
    }
    setDeleting(true);
    try {
      await deleteEvent(side.token, event.id);
      showToast('success', t('calendar.details.deleted_toast'), event.title);
      side.onClose();
      side.onDeleted(event.id);
    } catch (error: unknown) {
      // The row is already gone (another tab or a stale grid deleted it): the user's goal is
      // reached, so close cleanly on the success toast instead of crying failure - the server
      // answers 404 not_found for it.
      if (error instanceof CalendarApiError && error.status === 404) {
        showToast('success', t('calendar.details.deleted_toast'), event.title);
        side.onClose();
        side.onDeleted(event.id);
        return;
      }
      showToast('error', t('calendar.details.delete_failed'), event.title);
      setDeleting(false);
      setConfirmingDelete(false);
    }
  }

  /** Sidebar submit: create (POST) or edit (PATCH with replace/clear semantics). */
  async function submitSidebar(side: EventSidebarFormProps): Promise<void> {
    const capacityVisible = editing ? event?.capacity !== null : draft.kind === 'webinar';
    const { errors, capacityValue } = collectErrors({ capacityVisible });
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    setPending(true);
    try {
      if (editing && event !== null) {
        // PATCH: absent fields keep their stored value - the RRULE is never touched by this
        // form, so it stays absent. roomSlug is sent only when set: the service validates the
        // slug shape on every present PATCH field, so "" would answer 400 instead of clearing
        // (change request: a clear syntax for roomSlug) - the field is read-only now, so the
        // stored slug rides verbatim. The recording intent is no longer authored here: the
        // PATCH omits it entirely, so the stored value stays untouched. The lobby intent and
        // the description remain the form's authority (present replaces; the description
        // clears via ""), and the capacity rides only while its field is shown (present
        // replaces, 0 clears to unlimited).
        const storedSlug = draft.roomSlug.trim();
        const body: PatchEventBody = {
          title: draft.title.trim(),
          startsAt: toRfc3339(draftStartAt(draft)),
          endsAt: toRfc3339(draftEndAt(draft)),
          allDay: draft.allDay,
          location: draft.location.trim(),
          participants: [...participants],
          lobbyIntent: draft.lobbyIntent,
          description: draft.description.trim(),
          ...(event.capacity !== null ? { capacity: capacityValue ?? 0 } : {}),
          ...(storedSlug.length > 0 ? { roomSlug: storedSlug } : {}),
          ...(event.color === draft.color ? {} : { color: draft.color }),
        };
        const updated = await patchEvent(side.token, event.id, body);
        showToast('success', t('calendar.event.updated_toast'), updated.title);
        side.onClose();
        side.onSaved(updated);
        return;
      }
      const created = await createEvent(
        side.token,
        buildCreateBody({ capacityValue, participants }),
      );
      await ensureRoomAfterCreate(side.token);
      showToast('success', t('calendar.schedule.created_toast'), created.title);
      side.onClose();
      side.onSaved(created);
    } catch {
      showToast(
        'error',
        editing ? t('calendar.event.update_failed_toast') : t('calendar.schedule.failed_toast'),
      );
    } finally {
      setPending(false);
    }
  }

  /**
   * Room pre-registration (CR-26): an event with a room CREATES the room - the event exists at
   * this point, so the server resolves the event window and expires the room at its end + 2h;
   * the kind is write-once (ADR 0010) and the lobby option rides the same create. Awaited so
   * the room exists by the time the form reports the save; best effort otherwise.
   */
  async function ensureRoomAfterCreate(bearer: string): Promise<void> {
    const roomSlug = draft.onlineRoom ? draft.roomSlug.trim() : '';
    if (roomSlug.length === 0) {
      return;
    }
    await ensureRoomKind(bearer, roomSlug, draft.kind, { lobby: draft.lobbyIntent });
  }

  /** Dialog submit: create-only (POST + room pre-registration + toast + close). */
  async function submitDialog(dialog: DialogEventFormProps): Promise<void> {
    const { errors, capacityValue } = collectErrors({
      capacityVisible: draft.kind === 'webinar',
    });
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    setPending(true);
    try {
      const created = await createEvent(
        dialog.token,
        buildCreateBody({ capacityValue, participants }),
      );
      await ensureRoomAfterCreate(dialog.token);
      const when = `${draft.startDate} ${draft.startTime}`;
      dialog.onOpenChange(false);
      showToast(
        'success',
        t('calendar.schedule.created_toast'),
        t('calendar.schedule.created_toast_desc', { title: created.title, when }),
      );
      dialog.onCreated(created);
    } catch {
      showToast('error', t('calendar.schedule.failed_toast'));
    } finally {
      setPending(false);
    }
  }

  async function handleSubmit(formEvent: FormEvent<HTMLFormElement>): Promise<void> {
    formEvent.preventDefault();
    if (pending) {
      return;
    }
    if (props.variant === 'sidebar') {
      await submitSidebar(props);
    } else {
      await submitDialog(props);
    }
  }

  const disabled = readOnly || pending;
  const headerTitle = editing ? t('calendar.event.edit_title') : t('calendar.schedule.title');
  // Conflict watch (Outlook behavior): every draft window change recomputes the overlap
  // against the events already loaded for the visible range - no extra endpoint. The edited
  // series never conflicts with itself, and back-to-back rows stay silent (half-open windows).
  // Warn-only: the save button stays live. Imported (read-only) rows never warn; the dialog
  // variant passes no events, so it never warns either.
  const allEvents = props.variant === 'sidebar' ? (props.allEvents ?? []) : [];
  const conflicts = useMemo(
    () =>
      readOnly
        ? []
        : findOverlappingEvents(allEvents, draftStartAt(draft), draftEndAt(draft), event?.id),
    [allEvents, draft, event, readOnly],
  );
  // The lobby intent is room authoring metadata. Create: webinar-only (with the room gate on).
  // Edit: visible when the stored event links a room; a pre-registered lobbied room locks it
  // on (the server forces the lobby while the event is current - the create cannot downgrade).
  const roomSlugPresent = draft.roomSlug.trim().length > 0;
  const lobbyLocked = editing && event !== null && event.lobbyIntent && roomSlugPresent;
  const showLobby = editing ? roomSlugPresent : draft.kind === 'webinar' && draft.onlineRoom;
  const capacityVisible = editing ? event?.capacity !== null : draft.kind === 'webinar';
  const endInputShown = draft.allDay || draft.durationChoice === CUSTOM_DURATION;
  const roomStatusLine = roomManual
    ? null
    : roomGen.status === 'title_needed'
      ? t('calendar.event.room_title_needed')
      : roomGen.status === 'checking'
        ? t('calendar.event.room_status_checking')
        : roomGen.status === 'free'
          ? t('calendar.event.room_status_free')
          : roomGen.status === 'suggested'
            ? t('calendar.event.room_status_taken', { slug: roomGen.slug })
            : null;

  /** The auto-generated room block: read-only slug, status line, regenerate (create only). */
  function renderRoomAutoBlock(inputId: string): JSX.Element {
    return (
      <div className="mt-3">
        <Label htmlFor={inputId} className={LABEL_CLASS}>
          {t('calendar.event.room_slug_label')}
        </Label>
        <div className="mt-2 flex gap-2">
          <Input
            id={inputId}
            readOnly
            value={draft.roomSlug}
            data-testid="event-room-slug"
            className="h-10 flex-1 rounded-lg font-mono text-[13px]"
          />
          <Button
            type="button"
            variant="outline"
            className="h-10 shrink-0 gap-1.5 px-3 text-[12.5px]"
            data-testid="event-room-regenerate"
            aria-label={t('calendar.event.room_regenerate')}
            disabled={roomGen.slug.length === 0 || pending}
            onClick={regenerateRoomSlug}
          >
            <Dices aria-hidden className="size-4" />
          </Button>
        </div>
        {roomStatusLine !== null ? (
          <p
            data-testid="event-room-status"
            role="status"
            className="mt-1 text-[11px] text-muted-foreground"
          >
            {roomStatusLine}
          </p>
        ) : null}
      </div>
    );
  }

  if (props.variant === 'sidebar') {
    const side = props;
    return (
      <div className="flex min-h-0 flex-1 flex-col" data-testid="event-sidebar-form">
        <SheetHeader className="border-b border-border px-5 pb-4 pr-12 pt-5">
          <SheetTitle className="text-[17px] font-semibold tracking-tight text-foreground">
            {headerTitle}
          </SheetTitle>
          <SheetDescription className="flex flex-wrap items-center gap-2 text-[13px] text-muted-foreground">
            {sourceBadge !== null ? (
              <span
                className="rounded-md border border-border px-1.5 py-0.5 text-[10.5px]"
                data-testid="event-dialog-source"
              >
                {sourceBadge}
              </span>
            ) : null}
            {isOccurrence ? (
              <span data-testid="event-dialog-series-note">
                {t('calendar.event.series_note', {
                  date: new Intl.DateTimeFormat(locale, {
                    day: 'numeric',
                    month: 'long',
                  }).format(draftStartAt(draft)),
                })}
              </span>
            ) : (
              t('calendar.event.description')
            )}
          </SheetDescription>
        </SheetHeader>

        <form
          onSubmit={(formEvent) => void handleSubmit(formEvent)}
          noValidate
          className="flex min-h-0 flex-1 flex-col"
        >
          <fieldset
            disabled={disabled}
            className="scroll-thin min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-5"
          >
            {/* type cards: create-only (the event does not store the kind) */}
            {!editing ? <TypeCardsField active={draft.kind} onSelect={pickKind} /> : null}

            {/* title */}
            <TitleField
              id="event-title"
              label={t('calendar.schedule.name_label')}
              placeholder={t('calendar.schedule.name_placeholder')}
              value={draft.title}
              error={fieldErrors.title}
              onChange={(value) => setDraftField('title', value)}
            />

            {/* all-day */}
            <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2.5">
              <Label htmlFor="event-allday" className="text-[13px] text-foreground">
                {t('calendar.event.all_day')}
              </Label>
              <Switch
                id="event-allday"
                checked={draft.allDay}
                onCheckedChange={(checked) => setDraftField('allDay', checked)}
                data-testid="event-allday"
              />
            </div>

            {/* start: one DateTimeField (date + time; all-day hides the time parts) */}
            <div>
              <Label className={LABEL_CLASS}>{t('calendar.event.starts')}</Label>
              <div className="mt-2" data-testid="event-start-field">
                <DateTimeField
                  id="event-start"
                  date={draft.startDate}
                  time={draft.allDay ? '' : draft.startTime}
                  allDay={draft.allDay}
                  locale={isRu ? 'ru' : 'en'}
                  onChange={setStartDateTime}
                />
              </div>
            </div>

            {/* duration presets (no time/duration in the all-day form) */}
            {!draft.allDay ? (
              <div>
                <Label htmlFor="event-duration" className={LABEL_CLASS}>
                  {t('calendar.schedule.duration_label')}
                </Label>
                <select
                  id="event-duration"
                  data-testid="event-duration"
                  value={draft.durationChoice}
                  onChange={(input) => pickDuration(input.target.value)}
                  className="mt-2 h-10 w-full rounded-lg border border-border bg-card px-3 text-[13.5px] text-foreground outline-none focus:border-ring dark:[color-scheme:dark]"
                >
                  {DURATION_PRESETS.map((minutes) => (
                    <option key={minutes} value={`${minutes}`}>
                      {minutes < 60
                        ? t('calendar.schedule.duration_minutes', { count: minutes })
                        : t('calendar.schedule.duration_hours', { count: minutes / 60 })}
                    </option>
                  ))}
                  <option value={CUSTOM_DURATION}>{t('calendar.schedule.duration_custom')}</option>
                </select>
              </div>
            ) : null}

            {/* end: revealed for the custom duration and the all-day span */}
            {endInputShown ? (
              <div>
                <Label className={LABEL_CLASS}>{t('calendar.event.ends')}</Label>
                <div className="mt-2" data-testid="event-end-field">
                  <DateTimeField
                    id="event-end"
                    date={draft.endDate}
                    time={draft.allDay ? '' : draft.endTime}
                    allDay={draft.allDay}
                    locale={isRu ? 'ru' : 'en'}
                    onChange={setEndDateTime}
                  />
                </div>
                {fieldErrors.date !== undefined || fieldErrors.time !== undefined ? (
                  <FieldErrorText message={fieldErrors.date ?? fieldErrors.time} />
                ) : null}
                <FieldErrorText message={fieldErrors.range} />
              </div>
            ) : null}

            {/* conflict watch: warn-only, non-blocking (the save stays live) */}
            {conflicts.length > 0 ? (
              <div
                className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2.5"
                data-testid="event-conflict-warning"
                role="status"
              >
                <p className="flex items-center gap-1.5 text-[11.5px] font-semibold text-amber-400">
                  <TriangleAlert aria-hidden className="size-3.5" />
                  {t('calendar.event.conflict_title')}
                </p>
                <ul className="mt-1 space-y-0.5" data-testid="event-conflict-list">
                  {conflicts.map((conflict) => (
                    <li
                      key={eventKey(conflict)}
                      className="truncate text-[11.5px] text-amber-200/90"
                      data-testid="event-conflict-row"
                    >
                      {formatConflictRow(conflict, locale)}
                    </li>
                  ))}
                </ul>
                <p className="mt-1 text-[10.5px] text-amber-200/60">
                  {t('calendar.event.conflict_hint')}
                </p>
              </div>
            ) : null}

            {/* color: palette swatches + native picker + hex input, all synchronized */}
            <div>
              <Label className={LABEL_CLASS}>{t('calendar.event.color')}</Label>
              <div
                className="mt-2 flex flex-wrap items-center gap-2"
                role="group"
                aria-label={t('calendar.event.color')}
                data-testid="event-color-palette"
              >
                <button
                  type="button"
                  aria-pressed={draft.color.length === 0}
                  title={t('calendar.event.color_clear')}
                  data-testid="color-clear"
                  onClick={() => pickColor('')}
                  className={
                    draft.color.length === 0
                      ? 'size-7 rounded-full border-2 border-ring bg-transparent'
                      : 'size-7 rounded-full border border-dashed border-muted-foreground/50 bg-transparent hover:border-foreground'
                  }
                >
                  <span
                    aria-hidden
                    className="block text-center text-[12px] leading-none text-muted-foreground"
                  >
                    /
                  </span>
                </button>
                {EVENT_COLORS.map((hex) => {
                  const active = draft.color.toLowerCase() === hex.toLowerCase();
                  return (
                    <button
                      key={hex}
                      type="button"
                      aria-pressed={active}
                      aria-label={t('calendar.event.color_pick', { color: hex })}
                      title={hex}
                      data-testid={`color-swatch-${hex.toLowerCase()}`}
                      onClick={() => pickColor(hex)}
                      style={{ backgroundColor: hex }}
                      className={
                        active
                          ? 'size-7 rounded-full ring-2 ring-ring ring-offset-2 ring-offset-card'
                          : 'size-7 rounded-full ring-1 ring-white/10 transition-transform hover:scale-110'
                      }
                    />
                  );
                })}
                <input
                  type="color"
                  value={draft.color.length > 0 ? draft.color : DEFAULT_COLOR_PICKER_HEX}
                  onChange={(input) => pickColor(input.target.value)}
                  aria-label={t('calendar.event.color_picker')}
                  data-testid="event-color-picker"
                  className="size-7 shrink-0 cursor-pointer rounded-full border border-border bg-transparent p-0.5"
                />
                <span
                  aria-hidden
                  className="size-7 shrink-0 rounded-full border border-border"
                  style={{
                    backgroundColor: draft.color.length > 0 ? draft.color : 'transparent',
                  }}
                  data-testid="event-color-preview"
                />
              </div>
              <div className="mt-2 flex items-center gap-2">
                <Input
                  value={customColor}
                  onChange={(input) => {
                    setCustomColor(input.target.value);
                    setCustomColorError(null);
                  }}
                  onBlur={applyCustomColor}
                  onKeyDown={(keyEvent) => {
                    if (keyEvent.key === 'Enter') {
                      keyEvent.preventDefault();
                      applyCustomColor();
                    }
                  }}
                  placeholder="#7C4DFF"
                  aria-label={t('calendar.event.color_custom')}
                  aria-invalid={customColorError !== null || fieldErrors.color !== undefined}
                  className="h-9 w-40 rounded-lg font-mono text-[12.5px]"
                  data-testid="event-color-custom"
                />
                {customColorError !== null || fieldErrors.color !== undefined ? (
                  <p role="alert" className="text-[11.5px] text-red-400">
                    {customColorError ?? fieldErrors.color}
                  </p>
                ) : null}
              </div>
            </div>

            {/* location */}
            <LocationField
              id="event-location"
              label={t('calendar.schedule.location_label')}
              placeholder={t('calendar.schedule.location_placeholder')}
              value={draft.location}
              withIcon={false}
              onChange={(value) => setDraftField('location', value)}
            />

            {/* description: the long-form body the webinar detail page renders (CR-27) */}
            <DescriptionField
              id="event-description"
              testid="event-description"
              label={t('calendar.schedule.description_label')}
              placeholder={t('calendar.schedule.description_placeholder')}
              value={draft.description}
              error={fieldErrors.description}
              onChange={(value) => setDraftField('description', value)}
            />

            {/* capacity: webinar-only at create; at edit only when the stored event has one */}
            {capacityVisible ? (
              <CapacityField
                id="event-capacity"
                testid="event-capacity"
                label={t('calendar.schedule.capacity_label')}
                placeholder={t('calendar.schedule.capacity_placeholder')}
                value={draft.capacity}
                error={fieldErrors.capacity}
                onChange={(value) => setDraftField('capacity', value)}
              />
            ) : null}

            {/* online room: the gate + the auto-generated address (create) or the stored
                address read-only (edit; no toggle, no generation) */}
            <div>
              {editing ? (
                roomSlugPresent ? (
                  <div>
                    <Label htmlFor="event-room" className={LABEL_CLASS}>
                      {t('calendar.event.room_slug_label')}
                    </Label>
                    <Input
                      id="event-room"
                      readOnly
                      value={draft.roomSlug}
                      data-testid="event-room-slug"
                      className="mt-2 h-10 rounded-lg font-mono text-[13px]"
                    />
                  </div>
                ) : null
              ) : (
                <>
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <Label
                        htmlFor="event-online-room"
                        className="text-[12px] font-medium text-foreground"
                      >
                        {t('calendar.event.online_room_label')}
                      </Label>
                      <p className="text-[11px] text-muted-foreground">
                        {t('calendar.event.online_room_hint')}
                      </p>
                    </div>
                    <Switch
                      id="event-online-room"
                      checked={draft.onlineRoom}
                      onCheckedChange={setOnlineRoom}
                      data-testid="event-online-room"
                    />
                  </div>
                  {draft.onlineRoom ? renderRoomAutoBlock('event-room') : null}
                </>
              )}
              {showLobby ? (
                <div className="mt-3">
                  <LobbySwitchField
                    id="event-lobby-intent"
                    testid="event-lobby-intent"
                    checked={draft.lobbyIntent}
                    disabled={lobbyLocked}
                    hintTestid="event-lobby-hint"
                    hint={
                      lobbyLocked
                        ? t('calendar.event.lobby_locked_hint')
                        : t('calendar.schedule.lobby_hint')
                    }
                    onChange={(checked) => setDraftField('lobbyIntent', checked)}
                  />
                </div>
              ) : null}
            </div>

            {/* participants: the unified chips picker (CR-34) - directory suggestions while
                typing, free text on Enter/comma; the roster rules validated at add time */}
            <ParticipantsPickerField
              id="event-participants"
              token={token}
              participants={participants}
              onChange={setParticipants}
            />

            {readOnly ? (
              <p
                className="rounded-lg border border-border bg-muted/40 p-2.5 text-[11.5px] text-muted-foreground"
                data-testid="event-dialog-readonly"
              >
                {t('calendar.details.readonly', { source: sourceBadge ?? event?.source ?? '' })}
              </p>
            ) : null}
          </fieldset>

          <SheetFooter className="border-t border-border px-5 py-4 sm:justify-between">
            {editing && !readOnly ? (
              confirmingDelete ? (
                <div className="flex items-center gap-2" data-testid="event-delete-confirm">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 text-[12.5px]"
                    onClick={() => setConfirmingDelete(false)}
                  >
                    {t('calendar.details.close')}
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    className="h-8 gap-1 text-[12.5px]"
                    disabled={deleting}
                    onClick={() => void runSidebarDelete(side)}
                  >
                    {deleting ? (
                      <Loader2 aria-hidden className="size-3.5 animate-spin" />
                    ) : (
                      <Trash2 aria-hidden className="size-3.5" />
                    )}
                    {t('calendar.details.confirm_delete')}
                  </Button>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 gap-1.5 text-[12.5px] text-muted-foreground hover:text-red-400"
                  data-testid="event-delete"
                  onClick={() => setConfirmingDelete(true)}
                >
                  <Trash2 aria-hidden className="size-3.5" />
                  {t('calendar.details.delete')}
                </Button>
              )
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                className="h-9 border-border text-[13px]"
                onClick={side.onCancel}
              >
                {t('calendar.schedule.cancel')}
              </Button>
              {readOnly ? null : (
                <Button
                  type="submit"
                  disabled={pending}
                  className="h-9 gap-1.5 text-[13px] font-semibold"
                  data-testid="event-save"
                >
                  {pending ? <Loader2 aria-hidden className="size-3.5 animate-spin" /> : null}
                  {pending
                    ? t('calendar.event.saving')
                    : editing
                      ? t('calendar.event.save')
                      : t('calendar.schedule.submit')}
                </Button>
              )}
            </div>
          </SheetFooter>
        </form>
      </div>
    );
  }

  const dialog = props;
  return (
    <form onSubmit={(formEvent) => void handleSubmit(formEvent)} noValidate>
      <div className="space-y-5 px-6 py-5">
        {/* type selector: the card picks the room kind created for the event's slug
            (meeting/webinar, ADR 0010) plus the icon and defaults */}
        <TypeCardsField active={draft.kind} onSelect={pickKind} />

        {/* title */}
        <TitleField
          id="ev-title"
          label={t('calendar.schedule.name_label')}
          placeholder={t('calendar.schedule.name_placeholder')}
          value={draft.title}
          error={fieldErrors.title}
          onChange={(value) => setDraftField('title', value)}
        />

        {/* start: one DateTimeField (date + time) */}
        <div>
          <Label className={LABEL_CLASS}>{t('calendar.event.starts')}</Label>
          <div className="mt-2" data-testid="event-start-field">
            <DateTimeField
              id="ev-start"
              date={draft.startDate}
              time={draft.startTime}
              allDay={false}
              locale={isRu ? 'ru' : 'en'}
              onChange={setStartDateTime}
            />
          </div>
          <FieldErrorText message={fieldErrors.date} />
          <FieldErrorText message={fieldErrors.time} />
        </div>

        {/* duration: presets plus the custom escape hatch (reveals the end field) */}
        <div>
          <Label htmlFor="ev-duration" className={LABEL_CLASS}>
            {t('calendar.schedule.duration_label')}
          </Label>
          <select
            id="ev-duration"
            data-testid="event-duration"
            value={draft.durationChoice}
            onChange={(input) => pickDuration(input.target.value)}
            className="mt-2 h-10 w-full rounded-lg border border-border bg-card px-3 text-[13.5px] text-foreground outline-none focus:border-ring dark:[color-scheme:dark]"
          >
            {DURATION_PRESETS.map((minutes) => (
              <option key={minutes} value={`${minutes}`}>
                {minutes < 60
                  ? t('calendar.schedule.duration_minutes', { count: minutes })
                  : t('calendar.schedule.duration_hours', { count: minutes / 60 })}
              </option>
            ))}
            <option value={CUSTOM_DURATION}>{t('calendar.schedule.duration_custom')}</option>
          </select>
        </div>

        {/* end: revealed by the custom duration */}
        {draft.durationChoice === CUSTOM_DURATION ? (
          <div>
            <Label className={LABEL_CLASS}>{t('calendar.event.ends')}</Label>
            <div className="mt-2" data-testid="event-end-field">
              <DateTimeField
                id="ev-end"
                date={draft.endDate}
                time={draft.endTime}
                allDay={false}
                locale={isRu ? 'ru' : 'en'}
                onChange={setEndDateTime}
              />
            </div>
            <FieldErrorText message={fieldErrors.range} />
          </div>
        ) : null}

        {/* location */}
        <LocationField
          id="ev-location"
          label={t('calendar.schedule.location_label')}
          placeholder={t('calendar.schedule.location_placeholder')}
          value={draft.location}
          withIcon
          onChange={(value) => setDraftField('location', value)}
        />

        {/* description: the long-form body the webinar detail page renders (CR-27) */}
        <DescriptionField
          id="ev-description"
          testid="schedule-description"
          label={t('calendar.schedule.description_label')}
          placeholder={t('calendar.schedule.description_placeholder')}
          value={draft.description}
          error={fieldErrors.description}
          onChange={(value) => setDraftField('description', value)}
        />

        {/* capacity: webinar-only (the event-level registration cap, CR-27) */}
        {draft.kind === 'webinar' ? (
          <CapacityField
            id="ev-capacity"
            testid="schedule-capacity"
            label={t('calendar.schedule.capacity_label')}
            placeholder={t('calendar.schedule.capacity_placeholder')}
            value={draft.capacity}
            error={fieldErrors.capacity}
            onChange={(value) => setDraftField('capacity', value)}
          />
        ) : null}

        {/* online room: the gate + the auto-generated address + the AD rooms picker */}
        <div>
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <Label htmlFor="ev-online-room" className="text-[12px] font-medium text-foreground">
                {t('calendar.event.online_room_label')}
              </Label>
              <p className="text-[11px] text-muted-foreground">
                {t('calendar.event.online_room_hint')}
              </p>
            </div>
            <Switch
              id="ev-online-room"
              checked={draft.onlineRoom}
              onCheckedChange={setOnlineRoom}
              data-testid="event-online-room"
            />
          </div>
          {draft.onlineRoom ? (
            <>
              {renderRoomAutoBlock('ev-room')}
              <div className="mt-2 flex gap-2">
                <select
                  aria-label={t('calendar.schedule.room_pick')}
                  value=""
                  onChange={(input) => {
                    if (input.target.value.length > 0) {
                      pickAdRoom(input.target.value);
                    }
                  }}
                  disabled={roomsState !== 'ready' || rooms.length === 0}
                  className="h-10 w-full rounded-lg border border-border bg-card px-3 text-[13px] text-foreground outline-none focus:border-ring disabled:opacity-50 dark:[color-scheme:dark]"
                >
                  <option value="">{t('calendar.schedule.room_pick')}</option>
                  {rooms.map((room) => {
                    const slug = slugifyAdRoom(room);
                    return slug === null ? null : (
                      <option key={room.dn} value={slug}>
                        {room.name}
                      </option>
                    );
                  })}
                </select>
              </div>
              {roomsState === 'loading' ? (
                <p className="mt-1 text-[11px] text-muted-foreground">
                  {t('calendar.schedule.room_pick_loading')}
                </p>
              ) : roomsState === 'ready' && rooms.length === 0 ? (
                <p className="mt-1 text-[11px] text-muted-foreground">
                  {t('calendar.schedule.room_pick_empty')}
                </p>
              ) : null}
              {/* CR-26: an event with a room creates the room, and the room's lifetime follows
                  the event: it closes 2 hours after the event ends. */}
              {roomSlugPresent ? (
                <p
                  data-testid="schedule-room-ttl-hint"
                  className="mt-1 text-[11px] text-muted-foreground"
                >
                  {t('calendar.schedule.room_ttl_hint')}
                </p>
              ) : null}
            </>
          ) : null}
          {/* The waiting-room switch (CR-14/CR-21): webinar-only, gated by the online room. */}
          {draft.kind === 'webinar' && draft.onlineRoom ? (
            <div className="mt-3">
              <LobbySwitchField
                id="ev-lobby"
                testid="schedule-lobby-toggle"
                checked={draft.lobbyIntent}
                disabled={false}
                hint={t('calendar.schedule.lobby_hint')}
                onChange={(checked) => setDraftField('lobbyIntent', checked)}
              />
            </div>
          ) : null}
        </div>

        {/* participants: the unified chips picker (CR-34) - directory suggestions while
            typing, free text on Enter/comma/the add button */}
        <ParticipantsPickerField
          id="ev-participant"
          token={token}
          participants={participants}
          onChange={setParticipants}
        />
      </div>

      <DialogFooter className="border-t border-border px-6 py-4 sm:justify-between">
        <p className="hidden text-[11.5px] text-muted-foreground sm:block">
          {t('calendar.schedule.footer_note')}
        </p>
        <div className="flex w-full gap-2 sm:w-auto">
          <Button
            type="button"
            variant="outline"
            className="h-9 flex-1 border-border text-[13px] sm:flex-none"
            onClick={() => dialog.onOpenChange(false)}
          >
            {t('calendar.schedule.cancel')}
          </Button>
          <Button
            type="submit"
            disabled={pending}
            className="h-9 flex-1 gap-1.5 text-[13px] font-semibold sm:flex-none"
          >
            {pending ? <Loader2 aria-hidden className="size-3.5 animate-spin" /> : null}
            {pending ? t('calendar.schedule.submitting') : t('calendar.schedule.submit')}
          </Button>
        </div>
      </DialogFooter>
    </form>
  );
}
